import { AfterViewInit, Component, forwardRef, input, OnDestroy, signal, WritableSignal } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

interface RangeDate {
  startDate: string;
  endDate: string;
}

@Component({
  selector: 'pds-range-form',
  templateUrl: './range-form.component.html',
  styleUrls: ['./range-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RangeFormComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class RangeFormComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  interval = input<number | null>(null);

  disabled: boolean;
  changed!: (value: RangeDate) => void;
  touched!: () => void;
  form: FormGroup;
  destroyRef$: Subject<void>;
  minStartDate: WritableSignal<string>;
  maxEndDate: WritableSignal<string>;

  constructor() {
    this.disabled = false;
    this.form = new FormGroup({ startDate: new FormControl(''), endDate: new FormControl('') });
    this.destroyRef$ = new Subject<void>();
    this.minStartDate = signal<string>('');

    this.maxEndDate = signal<string>('');
  }

  ngOnInit() {
    if (!this.interval()) return;

    this.form.controls['startDate'].valueChanges.subscribe((startDate) => {
      this.maxEndDate.set(this.getRelativeDate((this.interval() || 0) + 1, new Date(startDate)));
    });

    this.form.controls['endDate'].valueChanges.subscribe((endDate) => {
      this.minStartDate.set(this.getRelativeDate(-((this.interval() || 0) - 1), new Date(endDate)));
    });
  }

  ngOnDestroy() {
    this.destroyRef$.next();
    this.destroyRef$.complete();
  }

  ngAfterViewInit() {
    this.form.valueChanges.pipe(takeUntil(this.destroyRef$)).subscribe((value) => this.changed(value));
  }

  writeValue(value: RangeDate | null) {
    if (!value) {
      this.form.patchValue({ startDate: null, endDate: null });
    } else {
      this.form.patchValue(value);
    }
  }

  registerOnChange(fn: (value: RangeDate) => void) {
    this.changed = fn;
  }

  registerOnTouched(fn: () => void) {
    this.touched = fn;
  }

  setDisabledState(disabled: boolean) {
    disabled ? this.form.disable() : this.form.enable();
    this.disabled = disabled;
  }

  getRelativeDate(daysOffset: number, baseDate: Date = new Date()): string {
    const date = new Date(baseDate);
    date.setTime(date.getTime() + daysOffset * 24 * 60 * 60 * 1000);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
